import type { ComputedRef, Ref } from 'vue';
import { pick } from 'lodash-es';
import {
  computed,
  ref,
  watch,
  watchEffect,
} from 'vue';

import type {
  IAffiliateProgramCommissionAllMerchantFields,
  IAffiliateProgramCommissionFormSchema,
  IAffiliateProgramCommissionFormSchemaBaseFields,
  IAffiliateProgramCommissionFormSchemaCpaFields,
  IAffiliateProgramCommissionFormSchemaPrimaryStepsFields,
  IAffiliateProgramCommissionFormSchemaRevenueShareFields,
  IAffiliateProgramCommissionItem,
  IAttributeOptions,
  ICommissionsConstantsResponse,
  IConditionVersion,
  IConditionVersions,
  ISelectedVersions,
  ITransactionType,
} from '@modules/affiliate-program-commissions/submodules/edit/types';
import type {
  FormFlattenKey,
  FormFlattenValue,
  FormGroup,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type {
  IFormSelectButtonInputOption,
  IFormSelectInputOption,
  IFormSelectInputOptionWithExtendedProps,
} from '@modules/forms/types';
import type { IAppConfigMerchant } from '@modules/merchant/types';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS } from '@modules/affiliate-program-commissions/constants';
import { AFFILIATE_PROGRAM_COMMISSIONS_ATTRIBUTES } from '@modules/affiliate-program-commissions/submodules/edit/constants';
import { useConfig } from '@modules/config/composables';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_SMART_FILTERS } from '@modules/smart-filters/constants';
import {
  assert,
  isOptionalNumber,
  isOptionalString,
  isString,
} from '@utils/guards';

export interface CommissionFormSchemaComposition {
  formSchema: Ref<FormSchema<IAffiliateProgramCommissionFormSchema>>;
  formGroups: Ref<FormGroup<IAffiliateProgramCommissionFormSchema>[]>;
  transactionType: Ref<string | undefined>;
  selectedProduct: Ref<string | undefined>;
  selectedVersions: ComputedRef<ISelectedVersions>;
  currentFirstGroupVersion: ComputedRef<IConditionVersions | undefined>;
  currentSecondGroupVersion: ComputedRef<IConditionVersions | undefined>;
  productOptions: Ref<IFormSelectButtonInputOption<string>[]>;
  firstGroupStepTransactionType: Ref<ITransactionType>;
  secondGroupStepTransactionType: Ref<ITransactionType>;
  onFormChange<
    T extends IAffiliateProgramCommissionFormSchema,
    K extends FormFlattenKey<T>,
  >(
    key: K,
    value: FormFlattenValue<T, K> | undefined,
  ): void;
}

export default function useCommissionFormSchema(
  commission: Ref<IAffiliateProgramCommissionItem | undefined>,
  merchantsList: Ref<IAppConfigMerchant[] | undefined>,
  constants: Ref<ICommissionsConstantsResponse | undefined>,
  isEdit: Ref<boolean>,
  isReadonly: Ref<boolean>,
  hideAttributes: Ref<boolean>,
): CommissionFormSchemaComposition {
  const { getTranslation } = useI18n();
  const { config } = useConfig();

  const selectedTransactionType = ref<string>();
  const selectedMerchantId = ref<number>();
  const selectedProductOption = ref<string>();
  const selectedFirstGroupVersion = ref<string>();
  const selectedSecondGroupVersion = ref<string>();

  const merchantId = computed(() => selectedMerchantId.value || commission.value?.merchant_id);

  const merchantOptions = computed<IFormSelectInputOption<number>[]>(() => {
    if (!merchantsList.value) {
      return [];
    }

    return merchantsList.value.map((item) => ({
      value: item.id,
      label: item.value || item.merchant_name,
    }));
  });

  const isAllMerchant = computed(() => (
    merchantOptions.value.find((item) => item.value === merchantId.value)?.label === 'All'
  ));

  const typeOfTransactionOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!constants.value) {
      return [];
    }

    return Object.entries(constants.value.constants.condition.type_of_transaction).map(([label, value]) => ({
      value,
      label,
    }));
  });

  const transactionType = computed<string | undefined>(() => (
    selectedTransactionType.value || commission.value?.type_of_transaction || typeOfTransactionOptions.value[0]?.value
  ));

  const firstGroupStepTransactionType = computed<ITransactionType>(() => {
    if (transactionType.value === 'cpl') {
      return 'cpl';
    }

    return 'cpa';
  });

  const secondGroupStepTransactionType = ref<ITransactionType>('revenue_share');

  const firstGroupVersionsOptions = computed<IFormSelectInputOptionWithExtendedProps<string>[]>(() => {
    if (!constants.value || !transactionType.value) {
      return [];
    }
    let options: IConditionVersion = {};

    if (firstGroupStepTransactionType.value === 'cpa' || transactionType.value === 'hybrid') {
      options = constants.value.constants.versions.cpa;
    } else if (firstGroupStepTransactionType.value === 'cpl') {
      options = constants.value.constants.versions.cpl;
    }

    return Object.entries(options).map(([label, value]) => ({
      label,
      value: label,
      disabled: !value.active,
      default: value.default,
    }));
  });

  const secondGroupVersionsOptions = computed<IFormSelectInputOptionWithExtendedProps<string>[]>(() => {
    if (!constants.value || !transactionType.value) {
      return [];
    }
    let options: IConditionVersion = {};

    if (secondGroupStepTransactionType.value === 'revenue_share') {
      options = constants.value.constants.versions.revenue_share;
    }

    return Object.entries(options).map(([label, value]) => ({
      label,
      value: label,
      disabled: !value.active,
      default: value.default,
    }));
  });

  const selectedVersions = computed<ISelectedVersions>(() => {
    const result: ISelectedVersions = {
      [firstGroupStepTransactionType.value]: selectedFirstGroupVersion.value
        || commission.value?.options?.[firstGroupStepTransactionType.value]?.version
        || firstGroupVersionsOptions.value?.find((item) => item.default)?.value,
      [secondGroupStepTransactionType.value]: selectedSecondGroupVersion.value
        || commission.value?.options?.[secondGroupStepTransactionType.value]?.version
        || secondGroupVersionsOptions.value?.find((item) => item.default)?.value,
    };

    if (!transactionType.value || transactionType.value === 'hybrid') {
      return result;
    }

    return pick(result, transactionType.value);
  });

  const currentFirstGroupVersion = computed<IConditionVersions | undefined>(() => selectedVersions.value[firstGroupStepTransactionType.value]);

  const currentSecondGroupVersion = computed<IConditionVersions | undefined>(() => selectedVersions.value[secondGroupStepTransactionType.value]);

  const sumByMerchantOptions = computed<IFormSelectInputOption<boolean>[]>(() => [{
    value: true,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_SUM_BY_MERCHANT_ALL_YES').value,
  }, {
    value: false,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_SUM_BY_MERCHANT_ALL_NO').value,
  }]);

  const profitMethodOptions = computed<IFormSelectInputOption<string>[]>(() => {
    if (!constants.value) {
      return [];
    }

    return constants.value.constants.condition.calculating_profit_method_hybrid.map((item) => ({
      value: item,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, `FORM_${item.toUpperCase()}`).value,
    }));
  });

  const accrualOfInterestOptions = computed<IFormSelectInputOption<boolean>[]>(() => [{
    value: true,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_ENABLE_UPPER_INTERVAL').value,
  }, {
    value: false,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_ENABLE_EACH_INTERVAL').value,
  }]);

  const negativeCommissionsOptions = computed<IFormSelectInputOption<boolean>[]>(() => [{
    value: true,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_ENABLE_NEGATIVE_COMMISSION').value,
  }, {
    value: false,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_DISABLE_NEGATIVE_COMMISSION').value,
  }]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const availableReadonlyProducts = computed<string[]>(() => {
    if (!isReadonly.value) {
      return [];
    }

    const products: string[] = [];

    if (currentSecondGroupVersion.value === 'v2') {
      if (commission.value?.options?.revenue_share?.ngr_formula_modifier) {
        products.push(commission.value.options.revenue_share.ngr_formula_modifier);
      }
    }

    if (currentSecondGroupVersion.value === 'v1' && commission.value?.condition_filters_attributes) {
      for (const filter of commission.value.condition_filters_attributes) {
        for (const attribute of filter.condition_steps_attributes) {
          if (attribute.product && !products.includes(attribute.product)) {
            products.push(attribute.product);
          }
        }
      }
    }

    return products;
  });

  const productOptions = computed<IFormSelectButtonInputOption<string>[]>(() => {
    if (!constants.value || !currentSecondGroupVersion.value) {
      return [];
    }

    let options: string[] = [];

    if (currentSecondGroupVersion.value === 'v2') {
      options = ['all'].concat(config.value?.products_list ?? []);
    }

    if (currentSecondGroupVersion.value === 'v1') {
      if (transactionType.value === 'revenue_share') {
        options = Object.keys(constants.value.constants.condition_steps.obtained_result.revenue_share[currentSecondGroupVersion.value]);
      } else if (transactionType.value === 'hybrid') {
        options = Object.keys(constants.value.constants.condition_steps.obtained_result.hybrid.revenue_share[currentSecondGroupVersion.value]);
      }
    }

    return options.map((product) => ({
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, `PRODUCTS.RS.${product}`).value,
      value: product,
    } as IFormSelectButtonInputOption<string>)).filter((item) => (
      isReadonly.value ? availableReadonlyProducts.value.includes(item.value) : true
    ));
  });

  const selectedProduct = computed<string | undefined>(() => (
    selectedProductOption.value || productOptions.value[0]?.value
  ));

  function getAttributeValue(option: IAttributeOptions): number {
    if (!commission.value) {
      return 0;
    }

    return commission.value.commissions_attributes.find((item) => (
      item.commission_type === option
    ))?.value || 0;
  }

  const baseFormSchema = computed<FormSchema<IAffiliateProgramCommissionFormSchemaBaseFields>>(() => ({
    merchant_id: {
      type: EFormInputTypes.SELECT,
      options: merchantOptions.value,
      readonly: isEdit.value,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_MERCHANT').value,
      required: true,
      value: merchantId.value,
    },
    type_of_transaction: {
      type: EFormInputTypes.SELECT,
      options: typeOfTransactionOptions.value,
      readonly: isEdit.value,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_TYPE_OF_TRANSACTION').value,
      required: true,
      value: transactionType.value,
    },
    title: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_TITLE').value,
      required: true,
      value: commission.value?.title,
    },
    description: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_DESCRIPTION').value,
      required: true,
      value: commission.value?.description,
    },
    front_country_filter_index: {
      type: EFormInputTypes.SLOT,
    },
    front_country_values: {
      type: EFormInputTypes.SLOT,
    },
  }));

  const baseAllMerchantsSchema = computed<FormSchema<Required<IAffiliateProgramCommissionAllMerchantFields>>>(() => ({
    sum_by_merchant_all: {
      type: EFormInputTypes.SELECT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_SUM_BY_MERCHANT_ALL_LABEL').value,
      options: sumByMerchantOptions.value,
      required: true,
      value: commission.value?.sum_by_merchant_all,
    },
  }));

  const primaryStepsSchema = computed<FormSchema<IAffiliateProgramCommissionFormSchemaPrimaryStepsFields>>(() => ({
    front_first_group_version: {
      type: EFormInputTypes.SELECT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_DEAL_TYPE_VERSION_LABEL').value,
      options: firstGroupVersionsOptions.value,
      required: true,
      readonly: isEdit.value,
      optionDisabled: 'disabled',
      value: currentFirstGroupVersion.value,
    },
    front_first_group_steps: {
      type: EFormInputTypes.SLOT,
    },
  }));

  const cpaGroupSchema = computed<FormSchema<IAffiliateProgramCommissionFormSchemaCpaFields>>(() => ({
    ...primaryStepsSchema.value,
    front_first_group_deals: {
      type: EFormInputTypes.SLOT,
    },
  }));

  const revenueShareSchema = computed<FormSchema<IAffiliateProgramCommissionFormSchemaRevenueShareFields>>(() => {
    const schema: FormSchema<IAffiliateProgramCommissionFormSchemaRevenueShareFields> = {
      accrual_of_interest_on_the_upper_interval: {
        type: EFormInputTypes.SELECT,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_UPPER_INTERVAL').value,
        options: accrualOfInterestOptions.value,
        required: true,
        value: commission.value?.accrual_of_interest_on_the_upper_interval ?? accrualOfInterestOptions.value[0]?.value,
      },
      transferring_a_negative_commission: {
        type: EFormInputTypes.SELECT,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_NEGATIVE_COMMISSION').value,
        options: negativeCommissionsOptions.value,
        required: true,
        value: commission.value?.transferring_a_negative_commission ?? negativeCommissionsOptions.value[0]?.value,
      },
      front_second_group_version: {
        type: EFormInputTypes.SELECT,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_DEAL_TYPE_VERSION_LABEL').value,
        options: secondGroupVersionsOptions.value,
        required: true,
        readonly: isEdit.value,
        optionDisabled: 'disabled',
        hideErrors: true,
        value: currentSecondGroupVersion.value,
        headerMessage: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_CHANGE_REVENUE_SHARE_VERSION_WARNING').value,
        headerMessageSeverity: 'warn',
      },
      front_second_group_version_description: {
        type: EFormInputTypes.SLOT,
      },
      front_second_group_steps: {
        type: EFormInputTypes.SLOT,
      },
      front_second_group_products: {
        type: EFormInputTypes.SLOT,
      },
    };

    for (const attribute of AFFILIATE_PROGRAM_COMMISSIONS_ATTRIBUTES) {
      schema[attribute] = {
        type: EFormInputTypes.NUMBER,
        label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, `FORM_${attribute.toUpperCase()}`).value,
        required: true,
        value: getAttributeValue(attribute),
        validation: {
          minValue: {
            value: 0,
          },
          maxValue: {
            value: 100,
          },
        },
      };
    }

    return schema;
  });

  const formSchema = computed<FormSchema<IAffiliateProgramCommissionFormSchema>>(() => {
    let schema: FormSchema<IAffiliateProgramCommissionFormSchema> = {
      ...baseFormSchema.value,
    };

    if (isAllMerchant.value) {
      schema = {
        ...schema,
        ...baseAllMerchantsSchema.value,
      };
    }

    switch (transactionType.value) {
      case 'cpl':
        schema = {
          ...schema,
          ...primaryStepsSchema.value,
        };
        break;
      case 'hybrid':
        schema = {
          ...schema,
          ...cpaGroupSchema.value,
          ...revenueShareSchema.value,
          calculating_profit_method: {
            type: EFormInputTypes.SELECT,
            label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FORM_CALCULATING_PROFIT_METHOD').value,
            options: profitMethodOptions.value,
            required: true,
            value: commission.value?.calculating_profit_method || profitMethodOptions.value[0]?.value,
          },
        };
        break;
      case 'revenue_share':
        schema = {
          ...schema,
          ...revenueShareSchema.value,
        };
        break;
      case 'cpa':
        schema = {
          ...schema,
          ...cpaGroupSchema.value,
        };
        break;
      default:
        return schema;
    }

    return schema;
  });

  const baseFormGroup = computed<FormGroup<IAffiliateProgramCommissionFormSchema>>(() => ({
    items: [
      'merchant_id',
      'type_of_transaction',
      'title',
      'description',
      ...(hideAttributes.value ? [] : AFFILIATE_PROGRAM_COMMISSIONS_ATTRIBUTES),
      'calculating_profit_method',
      'sum_by_merchant_all',
      'accrual_of_interest_on_the_upper_interval',
      'transferring_a_negative_commission',
    ],
  }));

  const countriesFormGroup = computed<FormGroup<IAffiliateProgramCommissionFormSchema>>(() => ({
    title: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'FILTER_COUNTRIES').value,
    items: [
      'front_country_filter_index',
      'front_country_values',
    ],
  }));

  const firstSubGroup = computed<FormGroup<IAffiliateProgramCommissionFormSchema>>(() => {
    const frontName = transactionType.value === 'hybrid'
      ? getTranslation(TRANSLATION_GROUP_SMART_FILTERS, 'TYPE_OF_TRANSACTION.CPA').value
      : getTranslation(TRANSLATION_GROUP_SMART_FILTERS, `TYPE_OF_TRANSACTION.${transactionType.value?.toUpperCase()}`).value;

    return {
      title: `${frontName} ${getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'STEPS_HEADER').value}`,
      items: transactionType.value === 'hybrid'
        ? [
            'front_first_group_version',
            'front_first_group_deals',
            'front_first_group_steps',
          ]
        : [
            'front_first_group_version',
            'front_first_group_deals',
            'front_first_group_steps',
            'front_second_group_version',
            'front_second_group_version_description',
            'front_second_group_products',
            'front_second_group_steps',
          ],
    };
  });

  const formGroups = computed<FormGroup<IAffiliateProgramCommissionFormSchema>[]>(() => {
    const groups: FormGroup<IAffiliateProgramCommissionFormSchema>[] = [
      baseFormGroup.value,
      countriesFormGroup.value,
    ];

    if (transactionType.value) {
      groups.push(firstSubGroup.value);

      if (transactionType.value === 'hybrid') {
        const frontName = getTranslation(TRANSLATION_GROUP_SMART_FILTERS, 'TYPE_OF_TRANSACTION.REVENUE_SHARE').value;

        groups.push({
          title: `${frontName} ${getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_COMMISSIONS, 'STEPS_HEADER').value}`,
          items: [
            'front_second_group_version',
            'front_second_group_version_description',
            'front_second_group_products',
            'front_second_group_steps',
          ],
        });
      }
    }

    return groups;
  });

  function onFormChange<
    T extends IAffiliateProgramCommissionFormSchema,
    K extends FormFlattenKey<T>,
  >(
    key: K,
    value: FormFlattenValue<T, K> | undefined,
  ): void {
    if (key === 'type_of_transaction') {
      assert(isOptionalString(value));
      selectedTransactionType.value = value;
    }

    if (key === 'merchant_id') {
      assert(isOptionalNumber(value));
      selectedMerchantId.value = value;
    }

    if (key === 'front_second_group_products') {
      assert(isOptionalString(value));
      selectedProductOption.value = value;
    }

    if (key === 'front_first_group_version') {
      assert(isString(value));
      selectedFirstGroupVersion.value = value;
    }

    if (key === 'front_second_group_version') {
      assert(isString(value));
      selectedSecondGroupVersion.value = value;
      selectedProductOption.value = undefined;
    }
  }

  watch(transactionType, () => {
    selectedProductOption.value = undefined;
  });

  watchEffect(() => {
    if (commission.value?.options?.revenue_share?.ngr_formula_modifier) {
      selectedProductOption.value = commission.value?.options?.revenue_share?.ngr_formula_modifier;
    }
  });

  return {
    firstGroupStepTransactionType,
    secondGroupStepTransactionType,
    selectedProduct,
    selectedVersions,
    currentFirstGroupVersion,
    currentSecondGroupVersion,
    productOptions,
    formSchema,
    formGroups,
    transactionType,
    onFormChange,
  };
}
